'use client';
import { merriweather } from '@/app/fonts';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useRouter } from 'next/navigation';
import { SubmitHandler, useForm } from 'react-hook-form';
import { signInWithRedirect, signOut, signUp } from 'aws-amplify/auth';
import { zodResolver } from '@hookform/resolvers/zod';

import { z } from 'zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from './ui/form';
import { CheckCircle, Circle, X, Eye, EyeOff } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  ageConfirmation: boolean;
}


const passwordRequirements = [
  { regex: /.{8,}/, text: 'At least 8 characters' },
  { regex: /[A-Z]/, text: 'At least one uppercase letter' },
  { regex: /[a-z]/, text: 'At least one lowercase letter' },
  { regex: /[0-9]/, text: 'At least one number' }
];

const nameSchema = z
  .string()
  .min(2, 'Name must be at least 2 characters')
  .regex(/^[A-Za-z]+$/, 'Name must contain only letters');

export const formSchema = z
  .object({
    email: z.string().email('Invalid email address'),
    firstName: nameSchema,
    lastName: nameSchema,

    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(/[A-Z]/, 'Must include an uppercase letter')
      .regex(/[a-z]/, 'Must include a lowercase letter')
      .regex(/[0-9]/, 'Must include a number'),
    confirmPassword: z
      .string()
      .min(8, 'Confirm Password must be at least 8 characters'),
    ageConfirmation: z.boolean().refine((val) => val === true, {
      message: 'You must confirm that you are 18 or older',
    }),
  })
  .superRefine(({ password, confirmPassword }, ctx) => {
    if (confirmPassword && password !== confirmPassword) {
      ctx.addIssue({
        code: 'custom',
        path: ['confirmPassword'],
        message: 'Passwords do not match',
      });
    }
  });

export function SignupForm({
  className,
  showOTPInput,
  ...props
}: React.ComponentProps<'form'> & {
  showOTPInput: (cognitoUsername: string) => void;
}) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleSignupWithGoogle = () => {
    signOut({ global: true }).then(() => {
      signInWithRedirect({
        provider: 'Google',
      });
    });
  };

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
      email: '',
      firstName: '',
      lastName: '',
      ageConfirmation: false,
    },
    mode: 'onTouched', // ✅ Errors only show after user interacts
    reValidateMode: 'onChange', // ✅ Revalidates confirmPassword on every password change
    shouldFocusError: true, // ✅ Focuses on first invalid input
  });

  const isPasswordValid = () =>
    passwordRequirements.every(({ regex }) => regex.test(password));

  const onSubmit: SubmitHandler<FormValues> = async values => {
    try {
      const signupOutput = await signUp({
        username: values.email,
        password: values.password,
        options: {
          userAttributes: {
            email: values.email,
            given_name: values.firstName,
            family_name: values.lastName,
          },
        },
      });

      if (signupOutput.nextStep.signUpStep == 'CONFIRM_SIGN_UP') {
        // showSignupOtp
        showOTPInput(signupOutput.userId || '');
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'An error occurred';
      toast.error(errorMessage);
    }
  };

  const password = form.watch('password');

  useEffect(() => {
    if (form.formState.touchedFields.confirmPassword) {
      form.trigger('confirmPassword'); // ✅ Forces confirmPassword validation on password change
    }
  }, [password, form]);

  return (
    <Form {...form}>
      <form
        className={cn('space-y-6', className)}
        {...props}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <div className={`${merriweather.className} space-y-1`}>
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      className='h-12 border-1 border-[#cdc4c1] rounded-full text-center placeholder:text-black md:text-xl text-lg md:placeholder:text-xl placeholder:text-lg'
                      placeholder="First name"
                      aria-label="First name"
                      {...field}
                      autoComplete="given-name"
                      data-testid="first-name-input"
                    />
                  </FormControl>
                  <FormMessage data-testid="first-name-error" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      className='h-12 border-1 border-[#cdc4c1] rounded-full text-center placeholder:text-black  md:text-xl text-lg md:placeholder:text-xl placeholder:text-lg'
                      placeholder="Last name"
                      aria-label="Last name"
                      {...field}
                      autoComplete="family-name"
                      data-testid="last-name-input"
                    />
                  </FormControl>
                  <FormMessage data-testid="last-name-error" />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    type="email"
                    placeholder="Email"
                    aria-label="Email address"
                    {...field}
                    autoComplete="email"
                    data-testid="email-input"
                    className='h-12 border-1 border-[#cdc4c1] rounded-full text-center placeholder:text-black md:text-xl text-lg md:placeholder:text-xl placeholder:text-lg'
                  />
                </FormControl>
                <FormMessage
                  className="min-h-[20px]"
                  data-testid="email-error"
                />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Password"
                      aria-label="Password"
                      {...field}
                      autoComplete="new-password"
                      data-testid="password-input"
                      className='h-12 border-1 border-[#cdc4c1] rounded-full text-center placeholder:text-black md:text-xl text-lg md:placeholder:text-xl placeholder:text-lg'
                    />
                    <button
                      type="button"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showPassword ? (
                        <EyeOff className='cursor-pointer' size={24} />
                      ) : (
                        <Eye className='cursor-pointer' size={24} />
                      )}
                    </button>
                  </div>
                </FormControl>
                <ul className="mt-2 text-sm">
                  {passwordRequirements.map(({ regex, text }) => {
                    const isValid = password.length > 0 && regex.test(password);
                    return (
                      <li key={text} className="flex items-center gap-1.5 mb-1">
                        {isValid ? (
                          <CheckCircle size={14} className="text-green-500" />
                        ) : (
                          <Circle size={14} className="text-gray-400" />
                        )}
                        <span className="text-md text-gray-600">{text}</span>
                      </li>
                    );
                  })}
                </ul>
                <FormMessage data-testid="password-error" />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <div className="relative">
                    <Input
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Re-enter password"
                      aria-label="Re-enter password"
                      {...field}
                      autoComplete="new-password"
                      data-testid="confirm-password-input"
                      className='h-12 border-1 border-[#cdc4c1] rounded-full text-center placeholder:text-black  md:text-xl text-lg md:placeholder:text-xl placeholder:text-lg'
                      style={{
                        fontSize: 20,
                      }}
                    />
                    <button
                      type="button"
                      aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className='cursor-pointer' size={24} />
                      ) : (
                        <Eye className='cursor-pointer' size={24} />
                      )}
                    </button>
                  </div>
                </FormControl>
                <FormMessage data-testid="confirm-password-error" />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="ageConfirmation"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-2 space-y-0 py-2">
                <FormControl>
                  <input
                    id="age-confirmation"
                    type="checkbox"
                    checked={field.value}
                    onChange={field.onChange}
                    className="mt-0.5 h-4 w-4 accent-[#cdc4c1] cursor-pointer"
                    data-testid="age-confirmation-checkbox"
                  />
                </FormControl>
                <div className="leading-none">
                  <label htmlFor="age-confirmation" className="text-sm text-gray-600 cursor-pointer select-none">
                    I certify that I am 18 years of age or older
                  </label>
                  <FormMessage data-testid="age-confirmation-error" />
                </div>
              </FormItem>
            )}
          />
          <Button
            data-testid="signup-btn"
            type="submit"
            className="w-full h-12 border-0 rounded-full text-center  md:text-xl text-lg bg-[#cdc4c1]"
            disabled={
              !isPasswordValid() ||
              !form.formState.isValid ||
              form.formState.isSubmitting
            }
            loading={form.formState.isSubmitting}
          >
            Create account
          </Button>
          <div className="text-center my-4">
            <span className="bg-background text-lg text-muted-foreground relative z-10 px-2">
              or
            </span>
          </div>
          <Button
            onClick={handleSignupWithGoogle}
            type="button" // to skip form validation on google login
            variant="outline"
            className="md:display-none flex items-center justify-center w-full h-12 border-1 border-[#cdc4c1] rounded-full text-center md:text-[20px] text-lg relative"
          >
            <img src="/google-icon.png" alt="" className="md:absolute md:left-6 select-none" width={50} />
            <p>Continue with Google</p>
          </Button>
          <p className='my-6 text-md text-justify'>By continuing with sign up, you agree to our <span className='underline'>
            <a href='/guidelines/forums'>Community Guidelines</a>
            </span>, {' '}
            <span className='underline'><a href='/privacy-policy'>Privacy Policy</a></span>,{' '}
            <span className='underline'><a href='/cookies-policy'>Cookie Policy</a></span>, and <span className='underline'><a href='/member-agreement'>Member Agreement.</a></span></p>
        </div>
        <div className="text-center text-md mb-2">
          Have an account?{' '}
          <a href="/login" className="underline underline-offset-4">
            Login
          </a>
        </div>
      </form>
    </Form>
  );
}
